import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components/macro'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Keyboard } from 'swiper'
import { TransformWrapper, TransformComponent } from '@pronestor/react-zoom-pan-pinch'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { SwiperNav } from './MaximizedGallery.Navigation'
import { ImgWrapper } from 'components/ImgContainer/ImgWrapper'

export const Slider = ({ initial = 0, images = [] }) => {
  const [activeIndex, setActiveIndex] = useState(initial)
  const [isIOS, setIsIOS] = useState(false)
  const [scale, setScale] = useState(1) // normalized logical scale
  const swiperRef = useRef(null)
  const transformRef = useRef(null)

  // Detect iOS for gesture handling
  useEffect(() => {
    const ua = navigator.userAgent
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      setIsIOS(true)
      const prevent = (e) => e.preventDefault()
      document.addEventListener('gesturestart', prevent, { passive: false })
      return () => document.removeEventListener('gesturestart', prevent)
    }
  }, [])

  // Enable swipe only when scale = 1 (fully zoomed out)
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.allowTouchMove = scale === 1
    }
  }, [scale])

  const swiperConfig = {
    modules: [Keyboard, Pagination],
    slidesPerView: 1,
    spaceBetween: 0,
    initialSlide: initial,
    keyboard: { enabled: true },
    onSwiper: (swiper) => (swiperRef.current = swiper),
    onSlideChange: (e) => setActiveIndex(e.activeIndex),
  }

  return (
    <StyleInjection>
      <SafeContainer $isIOS={isIOS}>
        <SwiperContainer>
          <Swiper {...swiperConfig}>
            {images?.length > 1 && <SwiperNav activeIndex={activeIndex} images={images} />}

            {images?.map((img, i) => (
              <SwiperSlide key={i}>
                <ZoomableImage>
                  <TransformWrapper
                    ref={transformRef}
                    initialScale={1}
                    minScale={1}       // prevent zoom out below normal
                    maxScale={3}       // maximum zoom in
                    pinch={{ step: 0.1 }}
                    panning={{ disabled: scale === 1 }}
                    onZoom={(ref) => {
                      const raw = ref?.state?.scale ?? 1
                      setScale(raw) // normalized: 1 = normal size
                    }}
                    onZoomStop={(ref) => {
                      let raw = ref?.state?.scale ?? 1
                      // snap close to 1
                      if (Math.abs(raw - 1) < 0.05) raw = 1
                      
                      if(raw < 1) {
                        ref.setTransform(0, 0, 1)
                        setScale(1)
                      }else setScale(raw)
                      if (raw === 1 && transformRef.current) {
                        transformRef.current.resetTransform()
                      }

                      if (swiperRef.current) {
                        swiperRef.current.allowTouchMove = raw === 1
                        swiperRef.current.update()
                      }
                    }}
                  >
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ImgWrapper
                         img={typeof img === 'string' ? img : img.url || img.Url} alt={`slide-${i}`} imgStyle={{
                          width: "auto",
                          height: "auto",
                          maxWidth: "95vw",
                          maxHeight: "80vh",
                          borderRadius: "12px",
                          objectFit: "contain",
                          userSelect: "none",
                          // -webkit-user-drag: none,
                        }}
                        style={{
                          width: 'fit-content'
                        }}
                        />
                        
                      {/* <Img
                        src={typeof img === 'string' ? img : img.url || img.Url}
                        alt={`slide-${i}`}
                        loading="lazy"
                        draggable={false}
                      /> */}
                    </TransformComponent>
                  </TransformWrapper>
                </ZoomableImage>
              </SwiperSlide>
            ))}
          </Swiper>
        </SwiperContainer>
      </SafeContainer>
    </StyleInjection>
  )
}

/* ---------------- Styled Components ---------------- */

const StyleInjection = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const SafeContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  ${({ $isIOS }) =>
    $isIOS &&
    `
    isolation: isolate;
    contain: layout paint;
    transform: none !important;
    -webkit-transform: none !important;
  `}
`

const SwiperContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const ZoomableImage = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: pan-y;
`

const Img = styled.img`
  width: auto;
  height: auto;
  max-width: 95vw;
  max-height: 80vh;
  border-radius: 12px;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
`
